import { useState } from 'react';
import { useBatch } from '@/contexts/BatchContext';
import { Button } from '@/components/ui/button';
import { CalendarClock, Plus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import LoadingSpinner, { InlineSpinner } from '@/components/ui/loading-spinner';
import BatchForm from '@/components/BatchForm';

interface BatchGuardProps {
  children: React.ReactNode;
}

const BatchGuard = ({ children }: BatchGuardProps) => {
  const { currentBatch, batches, loading, hasBatches, setCurrentBatch } = useBatch();
  const [isSelecting, setIsSelecting] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { toast } = useToast();

  // Determine if modal should be shown
  // Show modal only when:
  // 1. No batches exist (need to create first batch)
  // 2. Current batch is not set (BatchContext already handles localStorage preferences)
  // The BatchContext automatically handles:
  // - Single batch: sets as current and saves to localStorage
  // - Multiple batches with user preference: sets preferred batch as current
  // - Multiple batches without user preference: leaves currentBatch as null
  const shouldShowModal = !loading && (!hasBatches || !currentBatch);

  // Handle batch selection
  const handleBatchSelect = async (batchId: string) => {
    try {
      setIsSelecting(true);
      const batch = batches.find(b => b.id === batchId);
      if (batch) {
        await setCurrentBatch(batch);
        // Note: setCurrentBatch already stores the preference in localStorage
        toast({
          title: 'Batch Selected',
          description: `${batch.year} is now your current batch`,
        });
      }
    } catch (error) {
      console.error('Error selecting batch:', error);
      toast({
        title: 'Error',
        description: 'Failed to select batch. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSelecting(false);
    }
  };

  return (
    <>
      {/* Always render children */}
      {children}

      {/* Show loading overlay */}
      {loading && (
        <Dialog open={true}>
          <DialogContent className="sm:max-w-md [&>button]:hidden">
            <LoadingSpinner message="Initializing BayesPrep..." showMessage={true} />
          </DialogContent>
        </Dialog>
      )}

      {/* Show batch modal when needed */}
      {shouldShowModal && (
        <Dialog open={true}>
          <DialogContent className="sm:max-w-md [&>button]:hidden">
            <DialogHeader>
              <div className="flex flex-col items-center mb-4">
                <CalendarClock className="h-12 w-12 text-primary mb-2" />
                <DialogTitle className="text-center">
                  {!hasBatches ? 'Welcome to BayesPrep Admin Portal!' : 'Select Current Batch'}
                </DialogTitle>
              </div>
              <DialogDescription className="text-center">
                {!hasBatches
                  ? 'You need to create a batch before you can proceed. A batch represents a period for organizing students and classes.'
                  : 'Please select which batch you want to work with. Your selection will be remembered for future logins:'
                }
              </DialogDescription>
            </DialogHeader>

            {showCreateForm ? (
              <div className="space-y-4">
                <BatchForm
                  onSuccess={() => setShowCreateForm(false)}
                  showCancelButton={false}
                />
                <Button
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {hasBatches && (
                  <div className="space-y-2">
                    {batches.map((batch) => (
                      <Button
                        key={batch.id}
                        variant="outline"
                        className="w-full justify-start"
                        onClick={() => handleBatchSelect(batch.id)}
                        disabled={isSelecting}
                      >
                        {isSelecting ? (
                          <InlineSpinner size="sm" className="mr-2" />
                        ) : (
                          <CalendarClock className="h-4 w-4 mr-2" />
                        )}
                        {batch.year}
                      </Button>
                    ))}
                  </div>
                )}

                {!hasBatches ? (
                  <Button
                    onClick={() => setShowCreateForm(true)}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Batch
                  </Button>
                ) : (
                  <div className="pt-4 border-t">
                    <Button
                      onClick={() => setShowCreateForm(true)}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Batch
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default BatchGuard;
